/**
 * Effect presets for Reels video editor
 */

/**
 * Video filters
 */
export const VIDEO_FILTERS = [
  {
    id: 'none',
    name: 'None',
    filter: '',
    thumbnail: null,
  },
  {
    id: 'grayscale',
    name: 'Grayscale',
    filter: 'grayscale(100%)',
    thumbnail: null,
  },
  {
    id: 'sepia',
    name: 'Sepia',
    filter: 'sepia(80%)',
    thumbnail: null,
  },
  {
    id: 'vintage',
    name: 'Vintage',
    filter: 'sepia(40%) contrast(90%) brightness(110%)',
    thumbnail: null,
  },
  {
    id: 'cool',
    name: 'Cool',
    filter: 'hue-rotate(180deg) saturate(80%)',
    thumbnail: null,
  },
  {
    id: 'warm',
    name: 'Warm',
    filter: 'sepia(30%) saturate(140%) brightness(105%)',
    thumbnail: null,
  },
  {
    id: 'dramatic',
    name: 'Dramatic',
    filter: 'contrast(130%) brightness(90%)',
    thumbnail: null,
  },
  {
    id: 'fade',
    name: 'Fade',
    filter: 'contrast(80%) brightness(110%) saturate(80%)',
    thumbnail: null,
  },
  {
    id: 'noir',
    name: 'Noir',
    filter: 'grayscale(100%) contrast(120%) brightness(90%)',
    thumbnail: null,
  },
  {
    id: 'vivid',
    name: 'Vivid',
    filter: 'saturate(150%) contrast(110%)',
    thumbnail: null,
  },
  {
    id: 'muted',
    name: 'Muted',
    filter: 'saturate(50%) brightness(105%)',
    thumbnail: null,
  },
  {
    id: 'invert',
    name: 'Invert',
    filter: 'invert(100%)',
    thumbnail: null,
  },
];

/**
 * Color adjustment defaults
 */
export const COLOR_ADJUSTMENTS = {
  brightness: { min: 0, max: 200, default: 100, unit: '%' },
  contrast: { min: 0, max: 200, default: 100, unit: '%' },
  saturation: { min: 0, max: 200, default: 100, unit: '%' },
  hue: { min: -180, max: 180, default: 0, unit: 'deg' },
  temperature: { min: -100, max: 100, default: 0, unit: '' },
  tint: { min: -100, max: 100, default: 0, unit: '' },
  shadows: { min: -100, max: 100, default: 0, unit: '' },
  highlights: { min: -100, max: 100, default: 0, unit: '' },
  blur: { min: 0, max: 20, default: 0, unit: 'px' },
  sharpen: { min: 0, max: 100, default: 0, unit: '%' },
};

/**
 * Video effects (motion/animated)
 */
export const VIDEO_EFFECTS = [
  {
    id: 'none',
    name: 'None',
    category: 'none',
  },
  {
    id: 'zoom-in',
    name: 'Zoom In',
    category: 'motion',
    keyframes: [
      { time: 0, scale: 1 },
      { time: 1, scale: 1.2 },
    ],
  },
  {
    id: 'zoom-out',
    name: 'Zoom Out',
    category: 'motion',
    keyframes: [
      { time: 0, scale: 1.2 },
      { time: 1, scale: 1 },
    ],
  },
  {
    id: 'pan-left',
    name: 'Pan Left',
    category: 'motion',
    keyframes: [
      { time: 0, x: 10 },
      { time: 1, x: -10 },
    ],
  },
  {
    id: 'pan-right',
    name: 'Pan Right',
    category: 'motion',
    keyframes: [
      { time: 0, x: -10 },
      { time: 1, x: 10 },
    ],
  },
  {
    id: 'shake',
    name: 'Shake',
    category: 'motion',
    intensity: 5,
    frequency: 10,
  },
  {
    id: 'pulse',
    name: 'Pulse',
    category: 'motion',
    intensity: 1.05,
    frequency: 2,
  },
  {
    id: 'glitch',
    name: 'Glitch',
    category: 'stylized',
    intensity: 0.5,
  },
  {
    id: 'vhs',
    name: 'VHS',
    category: 'stylized',
  },
  {
    id: 'film-grain',
    name: 'Film Grain',
    category: 'stylized',
    intensity: 0.3,
  },
  {
    id: 'vignette',
    name: 'Vignette',
    category: 'stylized',
    intensity: 0.5,
  },
  {
    id: 'blur-edges',
    name: 'Blur Edges',
    category: 'stylized',
    radius: 100,
  },
];

/**
 * Transition presets
 */
export const TRANSITIONS = [
  {
    id: 'cut',
    name: 'Cut',
    duration: 0,
    category: 'basic',
  },
  {
    id: 'crossfade',
    name: 'Crossfade',
    duration: 0.5,
    category: 'basic',
  },
  {
    id: 'fade-to-black',
    name: 'Fade to Black',
    duration: 0.5,
    category: 'fade',
  },
  {
    id: 'fade-to-white',
    name: 'Fade to White',
    duration: 0.5,
    category: 'fade',
  },
  {
    id: 'slide-left',
    name: 'Slide Left',
    duration: 0.5,
    category: 'slide',
  },
  {
    id: 'slide-right',
    name: 'Slide Right',
    duration: 0.5,
    category: 'slide',
  },
  {
    id: 'slide-up',
    name: 'Slide Up',
    duration: 0.5,
    category: 'slide',
  },
  {
    id: 'slide-down',
    name: 'Slide Down',
    duration: 0.5,
    category: 'slide',
  },
  {
    id: 'wipe-left',
    name: 'Wipe Left',
    duration: 0.5,
    category: 'wipe',
  },
  {
    id: 'wipe-right',
    name: 'Wipe Right',
    duration: 0.5,
    category: 'wipe',
  },
  {
    id: 'zoom-in',
    name: 'Zoom In',
    duration: 0.5,
    category: 'zoom',
  },
  {
    id: 'zoom-out',
    name: 'Zoom Out',
    duration: 0.5,
    category: 'zoom',
  },
  {
    id: 'blur',
    name: 'Blur',
    duration: 0.5,
    category: 'stylized',
  },
  {
    id: 'spin',
    name: 'Spin',
    duration: 0.5,
    category: 'stylized',
  },
  {
    id: 'glitch',
    name: 'Glitch',
    duration: 0.3,
    category: 'stylized',
  },
];

/**
 * Text animation presets
 */
export const TEXT_ANIMATIONS = {
  entrance: [
    { id: 'none', name: 'None' },
    { id: 'fade-in', name: 'Fade In' },
    { id: 'slide-up', name: 'Slide Up' },
    { id: 'slide-down', name: 'Slide Down' },
    { id: 'slide-left', name: 'Slide Left' },
    { id: 'slide-right', name: 'Slide Right' },
    { id: 'zoom-in', name: 'Zoom In' },
    { id: 'bounce', name: 'Bounce' },
    { id: 'pop', name: 'Pop' },
    { id: 'typewriter', name: 'Typewriter' },
    { id: 'wave', name: 'Wave' },
    { id: 'blur-in', name: 'Blur In' },
    { id: 'flip', name: 'Flip' },
    { id: 'rotate', name: 'Rotate' },
  ],
  emphasis: [
    { id: 'none', name: 'None' },
    { id: 'pulse', name: 'Pulse' },
    { id: 'shake', name: 'Shake' },
    { id: 'bounce', name: 'Bounce' },
    { id: 'glow', name: 'Glow' },
    { id: 'color-cycle', name: 'Color Cycle' },
  ],
  exit: [
    { id: 'none', name: 'None' },
    { id: 'fade-out', name: 'Fade Out' },
    { id: 'slide-up', name: 'Slide Up' },
    { id: 'slide-down', name: 'Slide Down' },
    { id: 'slide-left', name: 'Slide Left' },
    { id: 'slide-right', name: 'Slide Right' },
    { id: 'zoom-out', name: 'Zoom Out' },
    { id: 'blur-out', name: 'Blur Out' },
  ],
};

/**
 * Audio effects
 */
export const AUDIO_EFFECTS = [
  {
    id: 'none',
    name: 'None',
    category: 'none',
  },
  {
    id: 'fade-in',
    name: 'Fade In',
    category: 'fade',
    duration: 1,
  },
  {
    id: 'fade-out',
    name: 'Fade Out',
    category: 'fade',
    duration: 1,
  },
  {
    id: 'echo',
    name: 'Echo',
    category: 'effect',
    delay: 0.3,
    decay: 0.5,
  },
  {
    id: 'reverb',
    name: 'Reverb',
    category: 'effect',
    roomSize: 0.5,
    dampening: 0.5,
  },
  {
    id: 'bass-boost',
    name: 'Bass Boost',
    category: 'eq',
    gain: 6,
  },
  {
    id: 'treble-boost',
    name: 'Treble Boost',
    category: 'eq',
    gain: 6,
  },
  {
    id: 'voice-enhance',
    name: 'Voice Enhance',
    category: 'eq',
  },
  {
    id: 'noise-reduction',
    name: 'Noise Reduction',
    category: 'cleanup',
    intensity: 0.5,
  },
  {
    id: 'normalize',
    name: 'Normalize',
    category: 'cleanup',
    targetDb: -3,
  },
];

/**
 * Speed presets
 */
export const SPEED_PRESETS = [
  { value: 0.25, label: '0.25x' },
  { value: 0.5, label: '0.5x' },
  { value: 0.75, label: '0.75x' },
  { value: 1, label: '1x (Normal)' },
  { value: 1.25, label: '1.25x' },
  { value: 1.5, label: '1.5x' },
  { value: 2, label: '2x' },
  { value: 4, label: '4x' },
];

/**
 * Aspect ratio presets for social media
 */
export const ASPECT_RATIOS = [
  { id: '9:16', name: 'Reels/TikTok (9:16)', width: 1080, height: 1920 },
  { id: '1:1', name: 'Square (1:1)', width: 1080, height: 1080 },
  { id: '4:5', name: 'Instagram Portrait (4:5)', width: 1080, height: 1350 },
  { id: '16:9', name: 'YouTube/Landscape (16:9)', width: 1920, height: 1080 },
  { id: '4:3', name: 'Standard (4:3)', width: 1440, height: 1080 },
  { id: '21:9', name: 'Cinematic (21:9)', width: 2560, height: 1080 },
];

/**
 * Export quality presets
 */
export const EXPORT_PRESETS = {
  'instagram-reels': {
    name: 'Instagram Reels',
    width: 1080,
    height: 1920,
    fps: 30,
    bitrate: 8000000,
    format: 'mp4',
    codec: 'h264',
  },
  'tiktok': {
    name: 'TikTok',
    width: 1080,
    height: 1920,
    fps: 30,
    bitrate: 8000000,
    format: 'mp4',
    codec: 'h264',
  },
  'youtube-shorts': {
    name: 'YouTube Shorts',
    width: 1080,
    height: 1920,
    fps: 30,
    bitrate: 10000000,
    format: 'mp4',
    codec: 'h264',
  },
  'instagram-story': {
    name: 'Instagram Story',
    width: 1080,
    height: 1920,
    fps: 30,
    bitrate: 6000000,
    format: 'mp4',
    codec: 'h264',
  },
  'facebook-reels': {
    name: 'Facebook Reels',
    width: 1080,
    height: 1920,
    fps: 30,
    bitrate: 8000000,
    format: 'mp4',
    codec: 'h264',
  },
  'linkedin': {
    name: 'LinkedIn',
    width: 1080,
    height: 1080,
    fps: 30,
    bitrate: 6000000,
    format: 'mp4',
    codec: 'h264',
  },
  'twitter': {
    name: 'Twitter/X',
    width: 1080,
    height: 1920,
    fps: 30,
    bitrate: 8000000,
    format: 'mp4',
    codec: 'h264',
  },
  'high-quality': {
    name: 'High Quality',
    width: 1080,
    height: 1920,
    fps: 60,
    bitrate: 15000000,
    format: 'mp4',
    codec: 'h264',
  },
  'gif': {
    name: 'Animated GIF',
    width: 480,
    height: 854,
    fps: 15,
    format: 'gif',
  },
};
